'use client';

import { useEffect, useRef, useState } from 'react';

export const YMM_DATA_EVENT = 'maiacard:ymm-data';
export const YMM_STUDENT_ATTR = 'data-student';
export const YMM_TOKEN_TYPE_ATTR = 'data-tokentype';
export const YMM_DOM_DATA_SELECTOR = `[${YMM_STUDENT_ATTR}][${YMM_TOKEN_TYPE_ATTR}]`;

export interface YmmDomData {
    student: string;
    tokenType: string;
}

export interface UseYmmDomDataResult {
    data: YmmDomData | null;
    gaveUp: boolean;
}

const DEFAULT_TIMEOUT_MS = 8000;

function isComplete(candidate: Partial<YmmDomData> | null | undefined): candidate is YmmDomData {
    return Boolean(candidate?.student && candidate?.tokenType);
}

function readFromDom(): YmmDomData | null {
    const el = document.querySelector(YMM_DOM_DATA_SELECTOR);
    if (!el) return null;

    const candidate = {
        student: el.getAttribute(YMM_STUDENT_ATTR) ?? '',
        tokenType: el.getAttribute(YMM_TOKEN_TYPE_ATTR) ?? '',
    };

    return isComplete(candidate) ? candidate : null;
}


export function useYmmDomData(timeoutMs = DEFAULT_TIMEOUT_MS): UseYmmDomDataResult {
    const [data, setData] = useState<YmmDomData | null>(null);
    const [gaveUp, setGaveUp] = useState(false);
    const resolvedRef = useRef(false);

    useEffect(() => {
        if (resolvedRef.current) return;

        const onEvent = (event: Event) => resolve((event as CustomEvent<Partial<YmmDomData>>).detail);
        const observer = new MutationObserver(() => resolve(readFromDom()));
        const timeoutId = setTimeout(() => {
            stopWatching();
            setGaveUp(true);
        }, timeoutMs);

        function stopWatching() {
            window.removeEventListener(YMM_DATA_EVENT, onEvent);
            observer.disconnect();
            clearTimeout(timeoutId);
        }

        function resolve(candidate: Partial<YmmDomData> | null | undefined) {
            if (resolvedRef.current || !isComplete(candidate)) return;
            resolvedRef.current = true;
            stopWatching();
            setData(candidate);
        }

        window.addEventListener(YMM_DATA_EVENT, onEvent);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [YMM_STUDENT_ATTR, YMM_TOKEN_TYPE_ATTR],
        });

        resolve(readFromDom());

        return stopWatching;
    }, [timeoutMs]);

    return { data, gaveUp };
}